import React, { useContext } from "react";
import { motion } from "framer-motion";
import emoji from "react-easy-emoji";
import StyleContext from "../../contexts/StyleContext";
import { greeting, socialMediaLinks } from "../../portfolio";
import SocialMedia from "../../components/socialMedia/SocialMedia";

export default function GreetingModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden" id="greeting">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20" />

      {/* Animated Orbs */}
      <motion.div
        className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-20 -right-20 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          x: [0, -50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="relative z-10 container mx-auto px-4 py-20 md:py-32"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 max-w-7xl mx-auto">

          {/* Main Hero Card - Large */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-8 lg:row-span-2 glass rounded-3xl p-8 md:p-12 backdrop-blur-xl bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl hover:shadow-primary-500/20 transition-all duration-300"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full text-sm font-semibold shadow-lg">
                  Available for opportunities
                </span>
              </div>

              <h1 className="text-2xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className={isDark ? "text-white" : "text-gray-900"}>
                  {greeting.title}
                </span>
                <motion.span
                  className="inline-block ml-4"
                  animate={{
                    rotate: [0, 14, -8, 14, -4, 10, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    repeatDelay: 1,
                  }}
                >
                  {emoji("👋")}
                </motion.span>
              </h1>

              <p className={`text-xl md:text-2xl mb-8 leading-relaxed max-w-3xl ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}>
                {greeting.subTitle}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-8">
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-primary-500/50 transition-all duration-300"
                >
                  Get in Touch
                </motion.a>
                <motion.a
                  href={greeting.resumeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-4 rounded-xl font-semibold border-2 transition-all duration-300 ${
                    isDark
                      ? "border-white/20 text-white hover:bg-white/10"
                      : "border-gray-300 text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  View Resume
                </motion.a>
              </div>

              {/* Stats Bar */}
              <div className="flex flex-wrap gap-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-3xl font-bold gradient-text">10</div>
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Years Experience
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold gradient-text">50+</div>
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Projects Completed
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold gradient-text">15+</div>
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Technologies
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Profile Image Card */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-4 aspect-square rounded-3xl overflow-hidden glass backdrop-blur-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 border border-white/20 dark:border-gray-700/20 shadow-2xl hover:shadow-accent-500/20 transition-all duration-300"
          >
            <div className="w-full h-full flex items-center justify-center p-8">
              <motion.div
                className="relative w-full h-full"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {/* Placeholder for profile image */}
                <div className="w-full h-full rounded-2xl bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center">
                  <span className="text-8xl">👨‍💻</span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Social Links Card */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-4 rounded-3xl glass backdrop-blur-xl bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl p-6 hover:shadow-primary-500/20 transition-all duration-300"
          >
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              Connect With Me
            </h3>
            <SocialMedia />
          </motion.div>

          {/* Quick Info Cards */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-4 rounded-3xl glass backdrop-blur-xl bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl p-6 hover:shadow-accent-500/20 transition-all duration-300"
          >
            <div className="text-4xl mb-2">📍</div>
            <h3 className={`text-lg font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
              Location
            </h3>
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Available Worldwide
            </p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="lg:col-span-4 rounded-3xl glass backdrop-blur-xl bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl p-6 hover:shadow-accent-500/20 transition-all duration-300"
          >
            <div className="text-4xl mb-2">💼</div>
            <h3 className={`text-lg font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
              Currently
            </h3>
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Software Engineer
            </p>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="flex justify-center mt-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Scroll to explore
            </span>
            <svg
              className={`w-6 h-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
